import { defineStore } from "pinia";
import { Ref, computed, ref } from "vue";
import { useBookingStepStore } from "./bookingStep";
import { getFoods } from "@/api/api";
import { mapItemToBookingItem } from "@/services/price";
import { Mode } from "@/api/types/mode.enum";

export class BookingItem {
  id!: string;
  activated!: boolean;
  quantity!: number;
  packagePrice!: string;
  unitPrice!: string;
  options!: BookingItem[];
  meta!: any;
}

export class ClientInfo {
  firstname!: string;
  lastname!: string;
}

/**
 * True when a persisted booking date is strictly before today (local time):
 * the event has passed, so the whole cart is stale and must not rehydrate.
 * Unparseable values return false — the restore's own NaN guard handles those.
 */
function isDateInPast(value: unknown): boolean {
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime() < startOfToday.getTime();
}

export const useBookingDataStore = defineStore("bookingData", () => {
  const date = ref<Date | undefined>();
  const priceMode = ref<string>("base");
  const discount = ref<number>(0);
  const services = ref(Array<BookingItem>());
  const foods = ref(Array<BookingItem>());
  const menu = ref(Array<BookingItem>());
  const drinks = ref(Array<BookingItem>());
  const options = ref(Array<BookingItem>());
  const clientInfo = ref<any>({});
  const foodOrder = ref<any>({});
  const bookingId = ref<string>("");
  // Set when the menu-choice deadline has passed (the surprise-menu cron has
  // locked the choice) — it doesn't wipe the booking, it just signals the
  // "Sélection du Chef" message in menu mode.
  const menuChoiceClosed = ref(false);
  const venue = ref<'lasalle' | 'serre' | ''>('');
  const venuesByDate = ref<Record<string, string[]>>({});
  const isGreenhouse = computed(() => venue.value === 'serre');
  const noonInviteToken = ref<string | null>(null);
  const noonAcknowledged = ref<boolean>(false);
  const appliedCouponCodes = ref<string[]>([]);

  // How long an in-progress booking survives in localStorage. There is no
  // active server-side "reservation" hold — the chosen date's real availability
  // is re-checked live while the wizard is open (useDateAvailabilityWatch) and
  // again, authoritatively, at checkout. So the cart is kept generously: only a
  // hygiene cap on inactivity, or the booking date having passed, drops it.
  const RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  // Build the step store first: it drops a cart left by a menu link before
  // this store reads it, whichever of the two the page builds first.
  const step = useBookingStepStore();
  const data = localStorage.getItem("booking_data");
  if (data !== null) {
    const jsonData = JSON.parse(data);
    // A persisted noon cart is never restorable: the invite token authorizing
    // it is session-only (bookingStep refuses to restore Noon mode for the
    // same reason), and rehydrated into a normal session the noon service
    // would be invisible in the wizard yet still priced and submitted. A
    // genuine noon re-entry resets the cart and rebuilds from the invite URL,
    // so dropping it here loses nothing.
    const hasNoonCart = Array.isArray(jsonData.services)
      && jsonData.services.some((s: any) => s?.id === 'noon');
    const withinRetention = typeof jsonData.timestamp === 'number'
      && jsonData.timestamp > Date.now() - RETENTION_MS;
    // Once the booking date is in the past the cart is moot — drop it and start
    // fresh from the date step (distinct from a still-future date being taken
    // mid-session, which keeps the cart and only re-picks the date).
    const datePassed = jsonData.date != null && isDateInPast(jsonData.date);
    // A cart carrying a booking id was saved by a menu link — only a pre-fix
    // one (still open in a tab) can still do that. It holds someone else's
    // booking: never restore it.
    const fromMenuLink = !!jsonData.id;
    if (!hasNoonCart && withinRetention && !datePassed && !fromMenuLink) {
      if (jsonData.date) {
        date.value = new Date(jsonData.date);
        if (isNaN(date.value as any) || !(date.value instanceof Date)) {
          date.value = undefined;
        }
      }

      if (jsonData.discount) {
        discount.value = jsonData.discount;
      }

      if (jsonData.priceMode) {
        priceMode.value = jsonData.priceMode;
      }

      if (jsonData.services) {
        services.value = jsonData.services;
      }

      if (jsonData.clientInfo) {
        clientInfo.value = jsonData.clientInfo;
      }

      if (jsonData.foods) {
        foods.value = jsonData.foods;
      }

      if (jsonData.menu) {
        menu.value = jsonData.menu;
      }

      if (jsonData.drinks) {
        drinks.value = jsonData.drinks;
      }

      if (jsonData.options) {
        options.value = jsonData.options;
      }

      if (jsonData.venue) {
        venue.value = jsonData.venue;
      }

      // Per-date venue availability is computed on the date step; restore it so
      // the type step still knows which venues are bookable after a reload
      // (otherwise venueAvailable() falls back to "all available").
      if (jsonData.venuesByDate) {
        venuesByDate.value = jsonData.venuesByDate;
      }

      appliedCouponCodes.value = jsonData.appliedCouponCodes ?? [];
    } else {
      // Stale (inactive too long, the date has passed, or an unrestorable noon
      // cart). Drop it: clear the persisted blob — which may hold customer PII
      // (name/email) — and the step, then restart from the date step.
      localStorage.removeItem('booking_data');
      localStorage.removeItem('booking_step');
      step.reset();
    }
  }

  function updateService(service: BookingItem) {
    console.log('updateService', service);
    const index = services.value.findIndex(s => s.id == service.id);
    if (index > -1) {
      services.value[index] = service;
    } else {
      services.value.push(service);
    }
    console.log('SERVICES', services.value);
    // A base-table-service change invalidates the food/menu/drink selections
    // (they were sized for its guest count). `noon` carries the same
    // guest-count semantics as `diner`, so it invalidates them the same way.
    if (service.id.includes("diner") || service.id === 'noon') {
      foods.value = [];
      menu.value = [];
      drinks.value = [];
    }
    persist();
  }

  function updateFoods(food: BookingItem) {
    console.log('updateFoods');
    const index = foods.value.findIndex(s => s.id == food.id);
    if (index > -1) {
      if (food.quantity == 0) {
        foods.value.splice(index, 1);
      } else {
        foods.value[index] = food;
      }
    } else {
      foods.value.push(food);
    }
    menu.value = [];
    persist();
  }

  function updateInternal(category: Ref<BookingItem[]>, item: BookingItem) {
    const index = category.value.findIndex(s => s.id == item.id);
    if (index > -1) {
      if (item.quantity == 0) {
        category.value.splice(index, 1);
      } else {
        category.value[index] = item;
      }
    } else {
      category.value.push(item);
    }
    persist();
  }

  function clearMenu() {
    menu.value = [];
    persist();
  }

  function updateMenu(item: BookingItem) {
    updateInternal(menu, item);
  }

  function updateDrinks(item: BookingItem) {
    updateInternal(drinks, item);
  }

  function updateOptions(item: BookingItem) {
    updateInternal(options, item);
  }

  // Clear only the selected date (and its price context) so the customer can
  // pick a new one, while the rest of the cart — services, menu, drinks,
  // options, client info and venue filter — is preserved. Used by the live
  // availability watcher when the chosen date is taken mid-session.
  function clearDateForReselection() {
    date.value = undefined;
    priceMode.value = 'base';
    discount.value = 0;
    persist();
  }

  // Clear the booking cart (items, client info, venue, pricing context and the
  // noon acknowledgment) WITHOUT expiring the session or wiping the date. Used
  // when entering a fresh, constrained flow — a menu link (fetchOrder) or a
  // noon invite opened by URL: the store hydrates any still-valid cart from
  // localStorage on construction, so an invite must drop that stale cart (e.g.
  // an evening `diner`/`logement` service the backend would 422, or a prior
  // acknowledgment) before applying its own locked date + venue. The date is
  // intentionally preserved — the invite sets it right after — and bookingId /
  // menu-cutoff flags are left untouched.
  function resetCart() {
    services.value = [];
    foods.value = [];
    menu.value = [];
    drinks.value = [];
    options.value = [];
    clientInfo.value = {};
    discount.value = 0;
    priceMode.value = 'base';
    venue.value = '';
    venuesByDate.value = {};
    noonAcknowledged.value = false;
    // A coupon applied in a prior booking context must not linger into the
    // fresh flow this reset opens (e.g. a noon invite).
    appliedCouponCodes.value = [];
    persist();
  }

  // freshSelection=false sets the date without treating it as a new normal-flow
  // pick — used in menu/noon mode, which load a fixed date and must not clear
  // the menu-cutoff state that a normal selection resets.
  function updateDate(thedate: Date, thePriceMode = 'base', thediscount = 0, freshSelection = true) {
    date.value = thedate;
    date.value.setHours(12);
    priceMode.value = thePriceMode;
    discount.value = thediscount;
    if (freshSelection) {
      // A fresh normal-flow date selection — clear any menu-cutoff state left
      // over from a prior menu-link session in the same tab so the "Sélection
      // du Chef" copy can't leak into the normal booking flow.
      menuChoiceClosed.value = false;
    }
    persist();
  }

  function updateClientInfo(ci: any) {
    clientInfo.value = ci;
    persist();
  }

  function setVenue(v: 'lasalle' | 'serre' | '') {
    venue.value = v;
    persist();
  }

  function setCouponCodes(codes: string[]) {
    appliedCouponCodes.value = codes;
    persist();
  }

  function getService(id: string) {
    return services.value.find(s => s.id == id);
  }

  function persist() {
    // A menu link loads someone's confirmed booking (often a link the organiser
    // shared with their guests) and re-fetches it from the URL token on every
    // load. It must never be saved: the booking page shares this localStorage
    // (same iframe on eauget.com) and would prefill the next booking with it.
    if (step.getMode() === Mode.Menu) return;
    const data = getData();
    localStorage.setItem('booking_data', JSON.stringify(data));
  }

  function getData() {
    return {
      date: date.value,
      discount: discount.value,
      priceMode: priceMode.value,
      services: services.value,
      foods: foods.value,
      menu: menu.value,
      drinks: drinks.value,
      options: options.value,
      clientInfo: clientInfo.value,
      venue: venue.value,
      venuesByDate: venuesByDate.value,
      appliedCouponCodes: appliedCouponCodes.value,
      timestamp: new Date().getTime(),
      id: bookingId.value
    }
  }

  async function fetchOrder(orderData: any) {
    console.log('fetch Order', orderData);
    // The menu page shows the linked booking only — never leftovers (dish
    // picks, services, contact details) from this visitor's own cart.
    resetCart();

    const foodItems = await getFoods();
    console.log('order data', orderData)
    console.log('order data2', Object.keys(orderData.data));
    if (foodItems?.success && foodItems.data) {
      foodOrder.value = orderData;
      const f = Array<BookingItem>();
      Object.keys(orderData.data).forEach((item, index) => {
        if (item.includes('service')) {
          const i = foodItems.data?.find(f => f.Id == item);
          console.log('found item', i, foodItems.data);
          if ((parseInt(Object.values(orderData.data)[index] as string) || 0) > 0) {
            f.push(mapItemToBookingItem(i, (parseInt(Object.values(orderData.data)[index] as string) || 0)));
          }
        }
      });
      // Menu mode loads a confirmed booking's fixed date — mark it non-fresh so
      // it doesn't reset the menu-cutoff flag we set from the order just below.
      updateDate(new Date(orderData.data.Date), 'base', 0, false);
      menuChoiceClosed.value = !!orderData.surprise;
      bookingId.value = orderData.data['#id'];
      foods.value = f;
      console.log('Loaded Foods', foods.value, date.value);
      if (orderData.alreadyChosen) {
        console.log('CHOSEN, also load plates', orderData.menu);
        const m = Object.entries(orderData.menu).map(e => ({ id: e[0], quantity: parseInt(e[1] as string) || 0, unitPrice: "0", packagePrice: "0", activated: true, options: [], meta: {} })).filter(f => f.quantity > 0);
        console.log('menu', m.filter(f => f.quantity > 0));
        menu.value = m;
      }

    }
    // Restore booking data
    console.log('booking values', orderData.booking);
    clientInfo.value = {
      allergy: orderData.booking.Allergie,
      comment: orderData.booking.Notes,
      email: orderData.booking.Email
    };
  }

  function setBookingId(id: string) {
    console.log('setBookingId', id);
    bookingId.value = id;
  }

  function canGoMenu2() {
    let canGo = false;
    if (date.value !== undefined) {
      canGo = foods.value.every((f: any) => {
        const d: any = new Date(f.meta['Fin menu']);
        if (!isNaN(d) && d instanceof Date) {
          return d > date.value!;
        }
        return false;
      });
    }
    step.allowMenu2 = canGo;
    return canGo;
  }

  function onlyFondue() {
    // return date.value?.getDay() == 2 && !(date.value?.getFullYear() == 2024 && date.value?.getMonth() == 11 && date.value?.getDate() == 24);
    return false;
  }

  // The base table service the menu and drinks steps size themselves on:
  // `diner` in the evening flow, `noon` in the invite flow (quantity = guest
  // count on both). Diner wins if both ever linger in one cart, so a stale
  // noon item can never resize an evening booking.
  function baseTableService() {
    return services.value.find(s => s.id == 'diner') ?? services.value.find(s => s.id == 'noon');
  }

  function canGoMenu() {
    const canGo = (baseTableService()?.quantity ?? 0) > 0;
    step.allowMenu = canGo;
    return canGo;
  }

  function canGoOptions() {
    return services.value.some(s => s.id == 'logement' && s.quantity > 0);

  }

  return { services, foods, menu, drinks, options, clientInfo, date, discount, priceMode, bookingId, menuChoiceClosed, venue, venuesByDate, isGreenhouse, noonInviteToken, noonAcknowledged, appliedCouponCodes, onlyFondue, baseTableService, canGoMenu, canGoOptions, canGoMenu2, clearMenu, clearDateForReselection, resetCart, setCouponCodes, setBookingId, fetchOrder, getData, updateService, updateFoods, updateMenu, updateDrinks, updateOptions, updateDate, getService, updateClientInfo, setVenue };
})
